import { memo } from "react";
import { Box, Button, Container, Stack, Typography } from "@mui/material";
import ErrorOutlineRounded from "@mui/icons-material/ErrorOutlineRounded";
import { isRouteErrorResponse, useRouteError } from "react-router-dom";
import logo from "../../Assets/images/logo.svg";
import MessageUsPanel from "../../Components/MessageForm/MessageUsPanel";

/**
 * The router's errorElement: shown when a page crashes. It used to be a bare
 * "Error". Renders outside the site layout (the layout may be what broke), so
 * it carries its own logo link home. The message form sends the error text
 * along, so a report reaches the team with what went wrong.
 */
const describe = (error: unknown): string => {
  if (isRouteErrorResponse(error)) return `${error.status} ${error.statusText}`.trim();
  if (error instanceof Error) return error.message;
  return typeof error === "string" ? error : "";
};

const RouteError = memo(() => {
  const error = useRouteError();
  const detail = describe(error).slice(0, 150);

  return (
    <Container maxWidth="md" sx={{ py: { xs: 3, md: 5 } }}>
      <Box component="a" href="/" sx={{ display: "inline-block", mb: { xs: 4, md: 6 } }} aria-label="SimplerHome home">
        <img src={logo} alt="SimplerHome" style={{ height: 36 }} />
      </Box>
      <Stack alignItems="center" textAlign="center" spacing={1.5} sx={{ mb: { xs: 4, md: 5 } }}>
        <ErrorOutlineRounded sx={{ fontSize: 64, color: "text.secondary" }} />
        <Typography variant="h4" component="h1" fontWeight={700}>
          Something went wrong on this page
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 480 }}>
          It's not you. Reloading usually fixes it. If it keeps happening, tell us below and we'll look into it.
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ pt: 1.5, width: { xs: "100%", sm: "auto" } }}>
          <Button variant="contained" onClick={() => window.location.reload()} sx={{ textTransform: "none" }}>
            Reload the page
          </Button>
          <Button variant="outlined" href="/" sx={{ textTransform: "none" }}>
            Go to the home page
          </Button>
        </Stack>
      </Stack>
      <MessageUsPanel
        align="center"
        topic="broken-page"
        title="Report the problem"
        intro="What were you trying to do? We'll get it fixed."
        defaultSubject={detail ? `Page error: ${detail}` : "Page error"}
        messagePlaceholder="e.g. I clicked 'Claim this home' and the page went blank."
        data-testid="route-error-message"
      />
    </Container>
  );
});

export default RouteError;
